import { ParamsProps, ReturnType } from "./types";
import request from "@utils/request";

const list = async (args: ParamsProps) => {
  try {
    const config: ReturnType = {
      url: "orders",
      method: "get",
      authorization: true,
      params: args?.params,
      cacheTTL: 60000,
    };
    const response = await request(config);
    return response;
  } catch (err: any) {
    throw new Error(err);
  }
};

const find = async (id: number | string) => {
  try {
    const config: ReturnType = {
      url: `orders/${id}`,
      method: "get",
      authorization: true,
    };
    const response = await request(config);
    return response;
  } catch (err: any) {
    throw new Error(err);
  }
};

const updateStatus = async (
  id: number | string,
  status: string,
  extraData?: any,
) => {
  try {
    const config: ReturnType = {
      url: `orders/status/${id}`,
      method: "patch",
      authorization: true,
      data: { status, ...extraData },
      config: {
        successMsg: "Order status updated successfully",
      },
    };
    const response = await request(config);
    return response;
  } catch (err: any) {
    throw new Error(err);
  }
};

const updatePaymentStatus = async (id: number | string, status: string) => {
  try {
    const config: ReturnType = {
      url: `orders/payment-status/${id}`,
      method: "patch",
      authorization: true,
      data: { status },
      config: {
        successMsg: "Payment status updated successfully",
      },
    };
    const response = await request(config);
    return response;
  } catch (err: any) {
    throw new Error(err);
  }
};

// Order Management Actions
const acceptOrder = async (id: number | string, processingTime: string) => {
  try {
    const config: ReturnType = {
      url: `orders/${id}/accept`,
      method: "post",
      authorization: true,
      data: { processingTime: processingTime.toString() },
      config: {
        successMsg: "Order accepted successfully",
      },
    };
    const response = await request(config);
    return response;
  } catch (err: any) {
    throw new Error(err);
  }
};

const rejectOrder = async (
  id: number | string,
  reasonId?: number,
  note?: string,
  status?: string,
) => {
  try {
    const config: ReturnType = {
      url: `orders/${id}/reject`,
      method: "post",
      authorization: true,
      data: { rejectionReasonId: reasonId, rejectionNote: note, status },
      config: {
        successMsg: status === "Cancelled" ? "Order cancelled successfully" : "Order rejected successfully",
      },
    };
    const response = await request(config);
    return response;
  } catch (err: any) {
    throw new Error(err);
  }
};

const assignDelivery = async (id: number | string, deliveryBoyId: number) => {
  try {
    const config: ReturnType = {
      url: `orders/${id}/assign-delivery`,
      method: "post",
      authorization: true,
      data: { deliveryBoyId },
      config: {
        successMsg: "Delivery assigned successfully",
      },
    };
    const response = await request(config);
    return response;
  } catch (err: any) {
    throw new Error(err);
  }
};

const getNearbyDeliveryBoys = async (id: number | string) => {
  try {
    const config: ReturnType = {
      url: `orders/${id}/nearby-delivery-boys`,
      method: "get",
      authorization: true,
    };
    const response = await request(config);
    return response;
  } catch (err: any) {
    throw new Error(err);
  }
};

const getCustomerOrderHistory = async (id: number | string) => {
  try {
    const config: ReturnType = {
      url: `orders/${id}/customer-history`,
      method: "get",
      authorization: true,
    };
    const response = await request(config);
    return response;
  } catch (err: any) {
    throw new Error(err);
  }
};

const editOrder = async (id: number | string, items: any[]) => {
  try {
    const config: ReturnType = {
      url: `orders/${id}/edit`,
      method: "put",
      authorization: true,
      data: { items },
      config: {
        successMsg: "Order updated successfully",
      },
    };
    const response = await request(config);
    return response;
  } catch (err: any) {
    throw new Error(err);
  }
};

const sendInvoice = async (
  id: number | string,
  payload: any[] | { items: any[]; customDiscount?: any },
) => {
  try {
    const config: ReturnType = {
      url: `orders/${id}/send-invoice`,
      method: "put",
      authorization: true,
      data: Array.isArray(payload) ? { items: payload } : payload,
      config: {
        successMsg: "Invoice shared with customer successfully",
      },
    };
    const response = await request(config);
    return response;
  } catch (err: any) {
    throw new Error(err);
  }
};

const acceptInvoice = async (id: number | string) => {
  try {
    const config: ReturnType = {
      url: `orders/${id}/accept-invoice`,
      method: "post",
      authorization: true,
      config: {
        successMsg: "Invoice accepted successfully",
      },
    };
    const response = await request(config);
    return response;
  } catch (err: any) {
    throw new Error(err);
  }
};

const rejectInvoice = async (id: number | string) => {
  try {
    const config: ReturnType = {
      url: `orders/${id}/reject-invoice`,
      method: "post",
      authorization: true,
      config: {
        successMsg: "Invoice rejected successfully",
      },
    };
    const response = await request(config);
    return response;
  } catch (err: any) {
    throw new Error(err);
  }
};

const createPOS = async (data: any) => {
  try {
    const config: ReturnType = {
      url: "orders",
      method: "post",
      authorization: true,
      data: { ...data, source: "POS" },
      config: {
        successMsg: "Order placed successfully",
        showErr: true,
      },
    };
    const response = await request(config);
    return response;
  } catch (err: any) {
    throw new Error(err);
  }
};

const trackEvent = async (id: number | string, event: string) => {
  try {
    const config: ReturnType = {
      url: `orders/${id}/track-event`,
      method: "post",
      authorization: true,
      data: { event },
      config: {
        showErr: false, // Don't show toast for background tracking
      },
    };
    const response = await request(config);
    return response;
  } catch (err: any) {
    console.error("Track event failed", err);
  }
};

export default {
  list,
  find,
  updateStatus,
  updatePaymentStatus,
  acceptOrder,
  rejectOrder,
  assignDelivery,
  getNearbyDeliveryBoys,
  getCustomerOrderHistory,
  editOrder,
  sendInvoice,
  acceptInvoice,
  rejectInvoice,
  createPOS,
  trackEvent,
};
